import type { TableColumnsType } from 'antd';
import {
  AlertBadge,
  SectionBox,
  StatusBadge,
  SignalBadge,
  Table,
  Text,
  CopyText,
  SvgIcon,
} from '@/components';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  complianceAlertsActions,
  complianceAlertStatsActions,
} from '@/redux/actions';
import {
  AlertListRequest,
  AlertListTableItem,
  AlertRuleType,
  CompAlertStatus,
  CompCustomerBrief,
  CompRiskLevel,
  UpdateAlertStatusRequest,
  BehaviorTx,
} from '@/api';
import {
  complianceAlertStatsSelectors,
  showDemoDataSelectors,
} from '@/redux/selectors';
import {
  formatUSD,
  getShortAddress,
  getFormattedDateWithTime,
} from '@/helpers';
import { RiskKeys } from '@/config';
import { TableActions } from '@/pages/ComplianceDashboard/Alerts/components/TableActions';
import { alertsDemoData } from '../../Alerts.constants';
import cx from 'classnames';
import {
  formatMinAmount,
  formatMaxAmount,
} from '@/pages/ComplianceDashboard/BehavioralAlerts/components/BehavioralAlertsItem/BehavioralAlertsItem.helpers';
import {
  BehavioralAlertsEntry,
  CategoriesValue,
} from '@/pages/ComplianceDashboard/BehavioralAlerts/components/BehavioralAlertsEntry/BehavioralAlertsEntry';

import styles from './Latest.module.sass';

const PAGE_SIZE = 15;

const Latest = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: alertStatsData } = useAppSelector(
    complianceAlertStatsSelectors.getComplianceAlertStatsSlice,
  );
  const isShowDemoData = useAppSelector(showDemoDataSelectors.byCompliance);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const data = useMemo(() => {
    return isShowDemoData ? alertsDemoData : alertStatsData;
  }, [isShowDemoData, alertStatsData]);

  const fetchList = useCallback(
    (page: number = 1) => {
      const offset = (page - 1) * PAGE_SIZE;
      const limit = PAGE_SIZE;
      const payload: AlertListRequest = {
        limit,
        offset,
      };
      dispatch(complianceAlertStatsActions.fetchAlertList(payload));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!isShowDemoData) {
      fetchList();
    }
  }, [isShowDemoData, fetchList]);

  const onStatusChange = useCallback(
    (id: string, status: CompAlertStatus) => {
      const payload: UpdateAlertStatusRequest = { id, status };

      dispatch(complianceAlertsActions.updateStatus(payload));
    },
    [dispatch],
  );

  const formatDirection = useCallback(
    (direction: string) => {
      if (!direction || direction === 'all') {
        return t('translation.all');
      }
      return direction === 'income'
        ? t('translation.deposit')
        : t('translation.withdrawal');
    },
    [t],
  );

  const columns: TableColumnsType<AlertListTableItem> = useMemo(
    () => [
      {
        title: t('translation.user'),
        dataIndex: 'customer',
        width: '13%',
        ellipsis: true,
        render: (customers: CompCustomerBrief[]) => {
          return (
            <div className={styles.customers}>
              {customers.map((customer) => (
                <Text key={customer.id} weight="medium" wrap="ellipsis">
                  {customer.identifier}
                </Text>
              ))}
            </div>
          );
        },
      },
      {
        title: t('translation.address'),
        dataIndex: 'address',
        width: '16%',
        ellipsis: true,
        render: (value) => (
          <CopyText text={getShortAddress(value)} copyText={value} />
        ),
      },
      {
        title: t('translation.direction'),
        dataIndex: 'direction',
        width: '9%',
        ellipsis: true,
        render: (value) => formatDirection(value),
      },
      {
        title: t('translation.risk'),
        dataIndex: 'risk',
        width: '11%',
        align: 'center',
        sorter: {
          compare: (a, b) => a.risk.length - b.risk.length,
        },
        render: (value) => <AlertBadge type={value} />,
      },
      {
        title: t('translation.risky_funds'),
        dataIndex: 'riskyFunds',
        width: '11%',
        sorter: {
          compare: (a, b) => a.riskyFunds - b.riskyFunds,
        },
        render: (value, record) => (!record.rule ? formatUSD(value) : null),
      },
      {
        title: t('translation.categories'),
        dataIndex: 'entityCategory',
        width: '11%',
        render: (value: RiskKeys[] | null) => {
          const list = Array.isArray(value) ? value : value ? [value] : null;
          if (!list?.length) return 'n/a';
          return (
            <span className={styles.categoryCell}>
              <span className={styles.categoryPillWrap}>
                <SignalBadge value={list[0]} className={styles.categoryPill} />
              </span>
              {list.length > 1 && (
                <span className={styles.categoryMore}>+{list.length - 1}</span>
              )}
            </span>
          );
        },
      },
      {
        title: t('translation.state'),
        dataIndex: 'state',
        width: '11%',
        render: (value) => <StatusBadge value={value} />,
      },
      {
        title: t('translation.date'),
        dataIndex: 'date',
        width: '10%',
        render: (value) => {
          // value is already formatted as "DD.MM.YYYY HH:mm"
          const parts = value.split(' ');
          const dateStr = parts[0] || value;
          const timeStr = parts[1] || '';
          return (
            <div>
              <Text wrap="ellipsis" color="gray">
                {dateStr}
              </Text>
              {timeStr && (
                <>
                  <br />
                  <Text wrap="ellipsis" color="gray">
                    {timeStr}
                  </Text>
                </>
              )}
            </div>
          );
        },
      },
      {
        title: '',
        dataIndex: 'actionButton',
        align: 'right',
        width: '7rem',
        render: (_, record) =>
          !isShowDemoData && (
            <TableActions record={record} onStatusChange={onStatusChange} />
          ),
      },
    ],
    [isShowDemoData, onStatusChange, t, formatDirection],
  );

  const formatBlockchain = (blockchain: string) => {
    return blockchain.toUpperCase();
  };

  const renderTransactions = (transactions: BehaviorTx[] = []) => {
    if (transactions.length === 0) {
      return (
        <Text color="gray" weight="medium">
          {t('translation.no_transactions')}
        </Text>
      );
    }

    return (
      <div className={styles.transactionsList}>
        {transactions.map((tx, index) => (
          <div key={`${tx.hash}-${index}`} className={styles.transactionItem}>
            <div className={styles.transactionField}>
              <Text
                weight="medium"
                color="gray"
                className={styles.transactionLabel}
              >
                {t('translation.date')}
              </Text>
              <Text className={styles.transactionValue}>
                {getFormattedDateWithTime(tx.createdDate)}
              </Text>
            </div>
            <div className={styles.transactionField}>
              <Text
                weight="medium"
                color="gray"
                className={styles.transactionLabel}
              >
                {t('translation.blockchain')}
              </Text>
              <Text className={styles.transactionValue}>
                {formatBlockchain(tx.blockchain)}
              </Text>
            </div>
            <div className={styles.transactionField}>
              <Text
                weight="medium"
                color="gray"
                className={styles.transactionLabel}
              >
                {t('translation.hash')}
              </Text>
              <CopyText
                text={getShortAddress(tx.hash)}
                copyText={tx.hash}
                className={styles.transactionValue}
              />
            </div>
            <div className={styles.transactionField}>
              <Text
                weight="medium"
                color="gray"
                className={styles.transactionLabel}
              >
                {t('translation.direction')}
              </Text>
              <Text className={styles.transactionValue}>
                {formatDirection(tx.direction)}
              </Text>
            </div>
            <div className={styles.transactionField}>
              <Text
                weight="medium"
                color="gray"
                className={styles.transactionLabel}
              >
                {t('translation.level')}
              </Text>
              <AlertBadge type={tx.level} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const hasExpandableData = (record: AlertListTableItem) => {
    return !!record.rule;
  };

  const expandedRowRender = (record: AlertListTableItem) => {
    const transactionsCount = record.behaviorTxs?.length || 0;
    const rule = record.rule;
    const params = rule?.params;
    const categories = record.entityCategory ?? [];
    const targetCustomerRisk =
      rule?.type === AlertRuleType.COMMON && params
        ? (params as { targetCustomerRisk?: CompRiskLevel }).targetCustomerRisk
        : undefined;
    const isSuspended =
      rule?.type === AlertRuleType.COMMON && params
        ? (params as { isSuspended?: boolean | null }).isSuspended
        : undefined;

    const hasTimePeriodInParams =
      params != null &&
      params.intervalValue != null &&
      params.intervalType != null;

    const formatTimePeriod = () => {
      if (!params || !hasTimePeriodInParams) return 'n/a';
      const { intervalValue, intervalType } = params;
      return `${intervalValue} ${intervalType}${intervalValue > 1 ? 's' : ''}`;
    };

    const formatAmountRange = () => {
      if (!params) return 'n/a';
      const { minAmount, maxAmount } = params;
      return `${formatMinAmount(minAmount)} – ${formatMaxAmount(maxAmount)}`;
    };

    const renderAmountRelatedEntry = () => {
      if (!params || !rule) return null;
      if (rule.type === AlertRuleType.TX_COUNT) {
        return (
          <BehavioralAlertsEntry
            label={t('translation.amount_range_usd')}
            value={formatAmountRange()}
          />
        );
      }
      if (rule.type === AlertRuleType.COMMON) {
        const minVolume = (params as { minVolume?: number }).minVolume;
        if (minVolume != null && minVolume > 0) {
          return (
            <BehavioralAlertsEntry
              label={t('translation.min_amount')}
              value={`≥ ${formatMinAmount(minVolume)}`}
            />
          );
        }
      }
      return null;
    };

    return (
      <div className={styles.expandedContent}>
        <div className={styles.detailsGroup}>
          {rule && (
            <>
              <BehavioralAlertsEntry
                label={t('translation.rule_name')}
                value={rule.name}
              />
              <BehavioralAlertsEntry
                label={t('translation.rule_type_label')}
                value={
                  rule.type === AlertRuleType.TX_COUNT
                    ? t('translation.rule_type_tx_count')
                    : t('translation.rule_type_common')
                }
              />
            </>
          )}
          {renderAmountRelatedEntry()}

          {isSuspended != null && (
            <BehavioralAlertsEntry
              label={t('translation.suspended')}
              value={isSuspended ? t('translation.yes') : t('translation.no')}
            />
          )}

          {targetCustomerRisk != null && (
            <BehavioralAlertsEntry
              label={t('translation.target_customer_risk')}
              value={<AlertBadge isTextOnly type={targetCustomerRisk} />}
            />
          )}

          {transactionsCount > 0 && (
            <BehavioralAlertsEntry
              label={t('translation.number_of_transfers')}
              value={transactionsCount.toString()}
            />
          )}

          {hasTimePeriodInParams && (
            <BehavioralAlertsEntry
              label={t('translation.time_period')}
              value={formatTimePeriod()}
            />
          )}

          {categories.length > 0 && (
            <BehavioralAlertsEntry
              label={t('translation.categories')}
              value={
                <CategoriesValue categories={categories} emptyDisplay="n/a" />
              }
            />
          )}

          {rule?.categoryJoinCondition != null && (
            <BehavioralAlertsEntry
              label={t('translation.category_join_label')}
              value={
                rule.categoryJoinCondition === 'all'
                  ? t('translation.category_join_all')
                  : t('translation.category_join_any')
              }
            />
          )}

          {params?.isBlacklisted != null && (
            <BehavioralAlertsEntry
              label={t('translation.check_if_counterparty_blacklisted')}
              value={
                params.isBlacklisted
                  ? t('translation.yes')
                  : t('translation.no')
              }
            />
          )}
        </div>
        {transactionsCount > 0 && (
          <div className={styles.transactionsSection}>
            <Text
              weight="medium"
              color="gray"
              className={styles.transactionsTitle}
            >
              {t('translation.involved_txs')}
            </Text>
            {renderTransactions(record.behaviorTxs)}
          </div>
        )}
      </div>
    );
  };

  return (
    <SectionBox>
      {{
        title: t('translation.latest_risky_alerts'),
        body: (
          <Table
            columns={columns}
            dataSource={data?.alerts?.entries}
            expandedRowRender={expandedRowRender}
            expandRowByClick={false}
            expandedRowKeys={expandedRowKeys}
            expandedRowClassName={() => styles.expandedRow}
            rowClassName={(record) => {
              const isExpanded = expandedRowKeys.includes(record.key);
              return isExpanded ? styles.expandedTableRow : '';
            }}
            onExpand={(expanded, record) => {
              if (expanded) {
                setExpandedRowKeys([...expandedRowKeys, record.key]);
              } else {
                setExpandedRowKeys(
                  expandedRowKeys.filter((key) => key !== record.key),
                );
              }
            }}
            expandIcon={({ expanded, onExpand, record }) => {
              const hasData = hasExpandableData(record);
              return (
                <button
                  type="button"
                  onClick={(e) => hasData && onExpand(record, e)}
                  className={styles.expandButton}
                  disabled={!hasData}
                >
                  <SvgIcon
                    name="arrow-right-short"
                    className={cx(styles.expandIcon, {
                      [styles.expanded]: expanded,
                    })}
                  />
                </button>
              );
            }}
            paginationConfig={{
              position: ['bottomLeft'],
              defaultCurrent: 1,
              pageSize: PAGE_SIZE,
              hideOnSinglePage: true,
              showSizeChanger: false,
              total: data?.alerts?.total || 0,
              onChange: (page) => fetchList(page),
            }}
          />
        ),
      }}
    </SectionBox>
  );
};

export default Latest;
